import React, { memo, useCallback } from 'react'

import { TagComponent as Tag } from '@cgtrader/triangle-ui/dist/src/ssr-index'

import Modal from 'Pods/Shared/Modal/Modal'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import { clearPendingGeneration } from 'Pods/ThreeDStudio/Services/pendingGenerationStorage'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'
import { closeUsageModal } from 'Pods/ThreeDStudio/Store/actions/ui'
import { STUDIO_ACCESS_TIER, USAGE_MODAL_REASON } from 'Pods/ThreeDStudio/Store/constants'

import useBreakpoint from 'Utils/useBreakpoint'

import EligibleForTrialBody from './Components/EligibleForTrialBody/EligibleForTrialBody'
import FreeBetaBody from './Components/FreeBetaBody/FreeBetaBody'
import HasCreditsBody from './Components/HasCreditsBody/HasCreditsBody'
import JoinWaitlistBody from './Components/JoinWaitlistBody/JoinWaitlistBody'
import SubscribedBody from './Components/SubscribedBody/SubscribedBody'
import UpgradeBody from './Components/UpgradeBody/UpgradeBody'
import WaitlistedBody from './Components/WaitlistedBody/WaitlistedBody'

const renderTag = (studioAccessTier, t) => {
  switch (studioAccessTier) {
    case STUDIO_ACCESS_TIER.IN_FREE_BETA:
      return <Tag className="usage-modal__tag">{t('usageModal.tags.freePlan')}</Tag>
    case STUDIO_ACCESS_TIER.SUBSCRIBED:
      return <Tag color="primary-24" className="usage-modal__tag">{t('usageModal.tags.subscription')}</Tag>
    default:
      return null
  }
}

function UsageModal() {
  const t = useTranslation()
  const isOpen = useThreeDStudioStore(state => state.ui.isUsageModalOpen)
  const usageModalReason = useThreeDStudioStore(state => state.ui.usageModalReason)
  const breakpoint = useBreakpoint()
  const isMobile = breakpoint === 'xs'
  const isMediumMobile = ['xs', 'sm'].includes(breakpoint)

  const studioAccessTier = useThreeDStudioStore(state => state.subscription.studioAccessTier)
  const monthlyUsed = useThreeDStudioStore(state => state.subscription.monthlyGenerationsUsed)
  const monthlyAvailable = useThreeDStudioStore(state => state.subscription.monthlyGenerationsAvailable)
  const availableCredits = useThreeDStudioStore(state => state.generation.availableCredits) || 0

  // A drained trial can coexist with credits granted outside it (admin,
  // top-up); those users can still generate, so they get the credits view
  // instead of the exhausted-trial warning and its full progress bar.
  const isTrialDrainedWithCredits = monthlyAvailable > 0
    && monthlyUsed >= monthlyAvailable
    && availableCredits > 0

  const handleClose = useCallback(() => {
    // Dismissing the modal (X / backdrop) abandons any pending post-auth generation, so a
    // later /ai visit does not silently replay it. The paid-subscription card redirects
    // instead of closing, so a real purchase never hits this path.
    clearPendingGeneration()
    closeUsageModal()
  }, [])

  if (!isOpen) {
    return null
  }

  // Opened from a locked control (Private mode, DCC Bridge, Export): show the
  // upgrade body with the advanced-features list for every tier that lacks
  // access, so this path must not depend on the tier-specific branching below
  // (which also returns null while the tier is unresolved).
  if (usageModalReason === USAGE_MODAL_REASON.PAID_FEATURE) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        width="small"
        header={t('usageModal.header.unlockAiGeneration')}
        customClass="usage-modal"
        ariaLabel={t('usageModal.header.unlockAiGeneration')}
      >
        <UpgradeBody onClose={handleClose} showAdvancedFeaturesList />
      </Modal>
    )
  }

  if (!studioAccessTier) {
    return null
  }

  if (studioAccessTier === STUDIO_ACCESS_TIER.ELIGIBLE_FOR_TRIAL) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        width={isMobile ? 'small' : 'medium'}
        header={t('usageModal.header.unlockAiGeneration')}
        customClass="usage-modal usage-modal--eligible-for-trial"
        ariaLabel="Unlock generation"
      >
        <EligibleForTrialBody onClose={handleClose} />
      </Modal>
    )
  }

  if (studioAccessTier === STUDIO_ACCESS_TIER.MUST_JOIN_WAITLIST) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        width={isMediumMobile ? 'small' : 'medium'}
        header={t('usageModal.header.unlockAiGeneration')}
        customClass="usage-modal usage-modal--eligible-for-trial"
        ariaLabel="Unlock generation"
      >
        <JoinWaitlistBody onClose={handleClose} />
      </Modal>
    )
  }

  if (studioAccessTier === STUDIO_ACCESS_TIER.WAITLISTED) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        width="small"
        header={t('usageModal.header.betaAccessRequested')}
        customClass="usage-modal usage-modal--waitlisted"
        ariaLabel="Beta access requested"
      >
        <WaitlistedBody onClose={handleClose} />
      </Modal>
    )
  }

  if (studioAccessTier === STUDIO_ACCESS_TIER.NOT_SUBSCRIBED) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        width="small"
        header={t('usageModal.header.unlockAiGeneration')}
        customClass="usage-modal"
        ariaLabel="Unlock generation"
      >
        <UpgradeBody onClose={handleClose} />
      </Modal>
    )
  }

  if (
    studioAccessTier !== STUDIO_ACCESS_TIER.IN_FREE_BETA
    && studioAccessTier !== STUDIO_ACCESS_TIER.HAS_CREDITS
    && studioAccessTier !== STUDIO_ACCESS_TIER.SUBSCRIBED
  ) {
    return null
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      width="small"
      header={t('usageModal.header.aiUsage')}
      headerAddon={renderTag(studioAccessTier, t)}
      customClass="usage-modal"
      ariaLabel="Usage"
    >
      {studioAccessTier === STUDIO_ACCESS_TIER.IN_FREE_BETA && (
        isTrialDrainedWithCredits
          ? <HasCreditsBody onClose={handleClose} />
          : <FreeBetaBody onClose={handleClose} />
      )}
      {studioAccessTier === STUDIO_ACCESS_TIER.HAS_CREDITS && (
        <HasCreditsBody onClose={handleClose} />
      )}
      {studioAccessTier === STUDIO_ACCESS_TIER.SUBSCRIBED && (
        <SubscribedBody onClose={handleClose} />
      )}
    </Modal>
  )
}

export default memo(UsageModal)
